// Activity domain types. Pure: no React, no Supabase.

import type { CustomFieldDefinition } from "@/domain/custom-fields/types";

export interface ActivityCategory {
  id: number;
  /** Stable join key clients group by; survives a rename of the Czech label. */
  slug: string;
  name: string;
  displayName: string;
  sortOrder: number;
}

export interface ActivitySubcategory {
  id: number;
  categoryId: number;
  displayName: string;
  sortOrder: number;
  category?: ActivityCategory | null;
}

/** Mirrors the `activities.booking_mode` check constraint. `interval` is readable but not
 *  authorable — see `authorableBookingModes`. */
export const bookingModes = ["session", "interval", "entry"] as const;

export type BookingMode = (typeof bookingModes)[number];

export const saleEndAnchors = ["start", "end"] as const;

export type SaleEndAnchor = (typeof saleEndAnchors)[number];

/** The two columns that only mean something together: an offset with no anchor
 *  has nothing to count from. */
export interface SaleClose {
  /** Signed real minutes from the anchor; negative is before it. */
  offsetMinutes: number;
  anchor: SaleEndAnchor;
}

export interface Activity {
  id: number;
  businessId: number;
  name: string;
  description: string | null;
  subcategoryId: number | null;
  subcategory?: ActivitySubcategory | null;
  bookingMode: BookingMode;
  priceOriginal: number | null;
  reservationUrl: string | null;
  imageUrl: string | null;
  /** Questions asked at checkout for every offer of this activity. Null = none. */
  customFields: CustomFieldDefinition[] | null;
  /** What a slevový termín published from this template is prefilled with, and
   *  then keeps a copy of (ADR 0007). Null means the partner names it each time.
   *  Never read back once a termín exists — editing the template must not reach
   *  past the lock A12 puts on a sold termín. */
  defaultPriceDiscounted: number | null;
  defaultCapacity: number | null;
  /** Hours before the start at which selling opens. Null leaves the fallback to
   *  the API's own default. */
  saleLeadTimeHours: number | null;
  /** Where selling stops, relative to the termín. Null on both leaves the end to
   *  the API's own default. They are stored and cleared as a pair. */
  saleEndOffsetMinutes: number | null;
  saleEndAnchor: SaleEndAnchor | null;
  /** Prefills the end of a termín from its start. Null = the partner names the
   *  end. A full day (1440) means the end equals the start, which the portal
   *  treats as no end to prefill. */
  defaultDurationMinutes: number | null;
  /** Picks the payment path for every termín published from this template:
   *  authorize and wait for the partner, rather than capture at once. ADR 0007
   *  refuses a per-termín override. */
  requiresConfirmation: boolean;
}
